import "./QuoteWidgetComponent.scss";
import template from "./QuoteWidgetComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {isNotNull} from "../../../../../js/util/UtilFunctions";
import {CodeAndExchange, securityEquality} from "app/instruments/types/CodeAndExchange";
import {QuoteWidgetConfig} from "./models/QuoteWidgetConfig";
import {QuoteStore} from "../../../../../quote/store/QuoteStore";
import {buildQuoteRequest} from "../../../../../quote/util/QuoteStoreUtil";
import {PortfolioType} from "../../../../../portfolio/models/Portfolio";
import {QuoteViewModel} from "../../../../../quote/models/QuoteViewModel";
import {CompanyStatisticsStore} from "../../../../../company-information/store/statistics/CompanyStatisticsStore";
import {IndustryGroup} from "../../../../../instruments/industry-group/types/IndustryGroup";
import {PlaceOrderService} from "../../../../../portfolio/templates/ticket/services/PlaceOrderService";
import {OrderSide} from "../../../../../orders/models/OrderRecord";
import {WidgetDisplayMode} from "../../../models/WidgetDisplayMode";

export const QuoteWidget = "tfQuoteWidget";

export function quoteWidget(): IComponentOptions {
    return {
        template,
        bindings: {
            security: "<",
            config: "<",
            displayMode: "<",
            onSecurityChanged: "&",
            onConfigChanged: "&"
        },
        controllerAs: "ctrl",
        controller: QuoteWidgetController
    };
}

export class QuoteWidgetController implements IOnInit, IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.security$.next(security);
        }
    }

    private readonly security$ = new ReplaySubject<CodeAndExchange>(1);

    /** @input */
    public set config(config: QuoteWidgetConfig | null) {
        if (config != null) {
            this.config$.next(config);
        }
    }

    private readonly config$ = new BehaviorSubject<QuoteWidgetConfig | null>(null);

    /** @input */
    public set displayMode(displayMode: WidgetDisplayMode | null) {
        if (displayMode != null) {
            this.displayMode$.next(displayMode);
        }
    }

    private readonly displayMode$ = new ReplaySubject<WidgetDisplayMode>(1);

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    /** @output */
    public readonly onConfigChanged: ComponentCallback;

    public readonly quote$: Observable<QuoteViewModel>;
    public readonly currentSecurity$ = new ReplaySubject<CodeAndExchange>(1);
    public readonly companyName$: Observable<string | null>;
    public readonly industryGroup$: Observable<IndustryGroup | null>;

    private readonly securityChanged$ = new ReplaySubject<CodeAndExchange>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    constructor(private readonly quoteStore: QuoteStore,
                private readonly companyStatisticsStore: CompanyStatisticsStore,
                private readonly placeOrderService: PlaceOrderService) {
        const security$ = this.currentSecurity$
                                              .takeUntil(this.destroyed$)
                                              .distinctUntilChanged(securityEquality());
        this.quote$ = security$.map((security) => buildQuoteRequest(security, PortfolioType.BROKER))
                               .switchMap((quoteRequest) => this.quoteStore.stream(quoteRequest))
                               .publishReplay(1)
                               .refCount();
        const securityInfo$ = security$.switchMap((sec) => this.companyStatisticsStore.stream(sec))
                                       .publishReplay(1)
                                       .refCount();
        this.companyName$ = securityInfo$.map((i) => i.companyName);
        this.industryGroup$ = securityInfo$.map((i) => i.industryGroup);
    }

    public $onInit(): void {
        this.watchSecurity();
        this.watchConfig();
        this.watchSecurityChanged();
    }

    private watchSecurity() {
        this.security$
            .takeUntil(this.destroyed$)
            .distinctUntilChanged()
            .withLatestFrom(this.config$)
            .map(([security, config]) => this.updateConfig(security, config))
            .subscribe((config) => this.configUpdated(config));
    }

    private watchConfig() {
        this.config$
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .map((c) => c.selected)
            .subscribe((security) => this.currentSecurity$.next(security));
    }

    private watchSecurityChanged() {
        this.securityChanged$
            .takeUntil(this.destroyed$)
            .distinctUntilChanged()
            .withLatestFrom(this.config$)
            .map(([security, config]) => this.updateConfig(security, config))
            .subscribe((config) => this.configUpdated(config));
    }

    private updateConfig(security: CodeAndExchange, config: QuoteWidgetConfig | null): QuoteWidgetConfig {
        if (config == null) {
            return {selected: security};
        } else {
            return {...config, selected: security};
        }
    }

    private configUpdated(config: QuoteWidgetConfig) {
        this.onConfigChanged({config});
    }

    public securityChanged(security: CodeAndExchange) {
        this.securityChanged$.next(security);
        this.onSecurityChanged({security});
    }

    public bidClicked(quote: QuoteViewModel) {
        this.openEquityTicket(quote, OrderSide.LONG_SELL);
    }

    public askClicked(quote: QuoteViewModel) {
        this.openEquityTicket(quote, OrderSide.BUY);
    }

    private openEquityTicket(quote: QuoteViewModel, side: OrderSide) {
        const code = quote.code;
        const limit = (side === OrderSide.BUY ? quote.quote.ask : quote.quote.bid) || 0;

        const order = {
            code,
            side,
            limit
        };

        this.placeOrderService.openEquityOrderWindow(order);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
